import React, { useEffect, useState } from "react";
import {
  Datagrid,
  DateField,
  DateInput,
  DeleteWithConfirmButton,
  EditButton,
  Filter,
  FunctionField,
  List,
  TextField,
  TextInput,
  useNotify,
  useRefresh,
  useListContext
} from "react-admin";
import { Checkbox } from "@material-ui/core";
import Toggle from "../../components/toggle/toggle";
import { useTokenOps } from "../../components/TokenContext";
import { hostname } from "../../providers/api";
import { useBcomeVersionRecordMap } from "../../components/DbVersion";
import { LcaVersionStatusIcon } from "../../components/LcaVersionStatus";
import { retainVisibleSelectedIds } from "./bulkSelection";
import "./ProjectList.css";

const Filters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="name" alwaysOn />
    <TextInput label="test2" source="name" defaultValue="test2" />
    <DateInput label="Creation" source="createdAt" />
  </Filter>
);

// Helper function to check if project version is compatible (>= 1.8.0)
const isCompatibleVersion = (version) => {
  if (!version) return false;
  const versionParts = version.split('.').map(Number);
  if (versionParts.length < 2) return false;

  const [major, minor] = versionParts;

  if (major > 1) return true;
  if (major === 1 && minor >= 8) return true;

  return false;
};

const SelectAllCheckbox = ({ selectedIds, compatibleProjectIds, onChange }) => {
  const allCompatibleSelected = compatibleProjectIds.length > 0 &&
    compatibleProjectIds.every(id => selectedIds.includes(id));

  const isIndeterminate = selectedIds.length > 0 && !allCompatibleSelected;

  return (
    <Checkbox
      checked={allCompatibleSelected}
      indeterminate={isIndeterminate}
      onChange={onChange}
      disabled={compatibleProjectIds.length === 0}
    />
  );
};

const BulkCalculationButton = ({ selectedIds, onCalculate, calculating, onCancel }) => {
  if (selectedIds.length === 0 && !calculating) return null;

  return (
    <div className="bulk-calculation-container">
      {calculating ? (
        <>
          <button className="bulk-calculation-btn cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="bulk-calculation-btn calculating-btn" disabled>
            <span className="spinner"></span>
            Calculating
          </button>
        </>
      ) : (
        <button
          className="bulk-calculation-btn"
          onClick={onCalculate}
          disabled={selectedIds.length === 0}
        >
          Bulk calculation
        </button>
      )}
    </div>
  );
};

const ProjectsListContent = () => {
  const { data, ids } = useListContext();
  const refresh = useRefresh();
  const notify = useNotify();
  const tokenOps = useTokenOps();
  const bcomeVersionRecordMap = useBcomeVersionRecordMap();

  const [selectedIds, setSelectedIds] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const updateViewProject = (id, isActive) => {
    const projectData = JSON.stringify({ isActive: !isActive });
    fetch(hostname + "/projects/editActiveProject/" + id, {
      method: "PATCH",
      body: projectData,
      headers: {
        "Content-type": "application/json",
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    }).then((response) => {
      if (response.status < 400) {
        refresh();
        notify("Updated successfully", "success");
      } else {
        notify(`Error: ${response.statusText}`, "warning");
      }
    });
  };

  const handleVisibility = (e) => {
    e.stopPropagation();
  };

  // Get list of compatible project IDs
  // In react-admin v3, data is an object with ids as keys
  const dataArray = ids ? ids.map(id => data[id]) : [];
  const compatibleProjectIds = dataArray
    .filter(project => project && isCompatibleVersion(project.version))
    .map(project => project.id);

  useEffect(() => {
    setSelectedIds(currentSelectedIds =>
      retainVisibleSelectedIds(currentSelectedIds, ids || [])
    );
  }, [ids]);

  // Check if a project is selected
  const isSelected = (id) => selectedIds.includes(id);

  // Toggle individual checkbox
  const handleToggleSelect = (id, event) => {
    event.stopPropagation();
    event.preventDefault();
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(itemId => itemId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Toggle "Select All Compatible"
  const handleSelectAllCompatible = (event) => {
    event.stopPropagation();
    event.preventDefault();

    // Use functional update to access the current state
    setSelectedIds(currentSelectedIds => {
      // Check if all compatible are currently selected
      const allSelected = compatibleProjectIds.length > 0 &&
        compatibleProjectIds.every(id => currentSelectedIds.includes(id));

      if (allSelected) {
        return [];
      } else {
        return compatibleProjectIds;
      }
    });
  };

  // Handle bulk calculation
  const handleBulkCalculation = async () => {
    const visibleSelectedIds = retainVisibleSelectedIds(selectedIds, ids || []);
    if (visibleSelectedIds.length === 0) return;

    setCalculating(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    try {
      const response = await fetch(`${hostname}/lca-supply/bulk-calculate`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          authorization: "Bearer " + tokenOps.account?.accessToken,
        },
        body: JSON.stringify({ projectIds: visibleSelectedIds }),
      });

      const responseData = await response.json();

      if (response.status < 400) {
        // Check if there are any failures in the results
        const failures = responseData.results?.filter(r => r.status === "failure") || [];
        const successes = responseData.results?.filter(r => r.status === "success") || [];

        if (failures.length > 0 && successes.length === 0) {
          // All failed
          setShowErrorMessage(true);
          setTimeout(() => setShowErrorMessage(false), 5000);
          failures.forEach(failure => {
            notify(`${failure.name}: ${failure.error}`, "warning");
          });
        } else if (failures.length > 0 && successes.length > 0) {
          // Partial success
          notify(`${successes.length} calculation(s) started successfully, ${failures.length} failed`, "info");
          failures.forEach(failure => {
            notify(`${failure.name}: ${failure.error}`, "warning");
          });
        } else {
          // All succeeded
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 5000);
          notify(`${successes.length} calculation(s) started successfully`, "success");
        }

        setSelectedIds([]);
        refresh();
      } else {
        const errorMessage = responseData.error || responseData.message || response.statusText;
        setShowErrorMessage(true);
        setTimeout(() => setShowErrorMessage(false), 5000);
        notify(`Bulk calculation failed: ${errorMessage}`, "error");
      }
    } catch (error) {
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 5000);
      notify(`Error: ${error.message}`, "error");
    } finally {
      setCalculating(false);
    }
  };

  // Handle cancel calculation
  const handleCancelCalculation = () => {
    setCalculating(false);
    setSelectedIds([]);
    notify("Calculation cancelled", "info");
  };

  return (
    <>
      <div style={{ marginTop: "15px", position: "relative" }}>
        <Datagrid rowClick="show">
          {/* Checkbox column */}
          <FunctionField
            key={`checkbox-column-${selectedIds.length}`}
            label={
              <SelectAllCheckbox
                selectedIds={selectedIds}
                compatibleProjectIds={compatibleProjectIds}
                onChange={handleSelectAllCompatible}
              />
            }
            render={(record) => {
              const compatible = isCompatibleVersion(record.version);
              return (
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected(record.id)}
                    disabled={!compatible}
                    onChange={(e) => handleToggleSelect(record.id, e)}
                  />
                </div>
              );
            }}
            sortable={false}
          />

          <TextField source="name" />
          <TextField source="id" />
          <TextField source="version" label="Version (legacy)" />
          <FunctionField
            label="DB Version"
            render={(record) =>
              bcomeVersionRecordMap[record.bcomeVersionId]?.databaseVersion?.name || "-"
            }
          />
          <FunctionField
            label="LCA Version"
            render={(record) =>
              bcomeVersionRecordMap[record.bcomeVersionId]?.lcaMethodologyVersion?.name || "-"
            }
          />
          <FunctionField
            label="LCA version check"
            render={(record) => (
              <LcaVersionStatusIcon lcaVersionStatus={record.lcaVersionStatus} />
            )}
            sortable={false}
          />
          <FunctionField
            label="Circularity Version"
            render={(record) =>
              bcomeVersionRecordMap[record.bcomeVersionId]?.circularityVersion?.circularity_version_name || "-"
            }
          />

          <TextField source="referenceType" label="References type" />
          <TextField source="functionalUnit" label="Functional unit" />

          <DateField source="createdAt" label="Creation date" />
          <DateField source="updatedAt" label="Update date" />
          <FunctionField
            onClick={handleVisibility}
            label="Project visibility"
            source="isActive"
            render={(item) => (
              <Toggle
                checked={item.isActive}
                onChange={() => updateViewProject(item.id, item.isActive)}
                className="ml-2"
              />
            )}
          />
          <TextField source="dataCollectionURL" label="Data collection url" required={false} />
          <TextField source="folderURL" label="Folder url" required={false} />
          <TextField source="status" label="Status" />
          <DateField source="closingDate" required={false} />

          <EditButton />
          <DeleteWithConfirmButton
            confirmTitle="Eliminar proyecto"
            confirmContent="¿Está seguro que desea eliminar este proyecto? Esta acción no se puede deshacer."
            label="Eliminar"
          />
        </Datagrid>

        {/* Bulk calculation button */}
        <BulkCalculationButton
          selectedIds={selectedIds}
          onCalculate={handleBulkCalculation}
          calculating={calculating}
          onCancel={handleCancelCalculation}
        />

        {/* Success message */}
        {showSuccessMessage && (
          <div className="bulk-message success-message">
            <span className="message-icon">✓</span>
            Bulk calculation completed successfully.
            <button
              className="close-message-btn"
              onClick={() => setShowSuccessMessage(false)}
            >
              ×
            </button>
          </div>
        )}

        {/* Error message */}
        {showErrorMessage && (
          <div className="bulk-message error-message">
            <span className="message-icon">⚠</span>
            Bulk calculation failed. Please try again.
            <button
              className="close-message-btn"
              onClick={() => setShowErrorMessage(false)}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </>
  );
};

const ProjectsList = (props) => {
  return (
    <List {...props} filters={Filters(props)} bulkActionButtons={<Toggle />}>
      <ProjectsListContent />
    </List>
  );
};

export default ProjectsList;
